import type { DemPoExtractOrder, DemPoExtractRow } from '../types/dem';
import { parseDeliveryDateDmy } from './demDeliveryDate';
import { isExcelFileName, readExcelRowsForExtract } from './demPoExcel';

type ColumnMap = {
  items?: number;
  description?: number;
  quantity?: number;
  uom?: number;
  unit_price?: number;
};

type ConsolidationColumnMap = ColumnMap & {
  outlet: number;
  po_number: number;
  delivery_date?: number;
};

/** FoodMarketHub / similar vendor SKU codes, e.g. P0045C, S0001D */
export const PO_VENDOR_SKU_RE = /\b([A-Z]\d{4}[A-Z])\b/;

export function vendorSkuRowKey(row: {
  items: string;
  description?: string;
  quantity: string | number;
  uom?: string;
  unit_price?: string;
}): string {
  const qty = String(row.quantity).replace(/[^\d.]/g, '');
  const price = String(row.unit_price ?? '')
    .replace(/[^\d.]/g, '')
    .trim();
  const uom = String(row.uom ?? '')
    .trim()
    .toUpperCase();
  const items = String(row.items ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ');
  const description = String(row.description ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ');
  const raw = `${items} ${description}`.trim();
  const match = raw.match(PO_VENDOR_SKU_RE);
  if (match) {
    // Include description + price so intentional re-orders of the same vendor SKU still stay.
    return `${match[1]!.toUpperCase()}|${description}|${qty}|${uom}|${price}`;
  }
  if (items || description) {
    return `${items}|${description}|${qty}|${uom}|${price}`;
  }
  return `|${qty}|${uom}|${price}`;
}

function cellAt(row: string[], index: number): string {
  return String(row[index] ?? '').trim();
}

export function findSkuInRow(row: string[]): string {
  for (let i = 0; i < row.length; i += 1) {
    const match = cellAt(row, i).match(PO_VENDOR_SKU_RE);
    if (match) return match[1]!.toUpperCase();
  }
  return '';
}

const UOM_TOKENS = new Set(['PKT', 'CTN', 'PCS', 'BOX', 'SET', 'ROLL', 'BAG', 'EA', 'UNIT', 'PCK']);

function parseQty(raw: string): number {
  const cleaned = String(raw ?? '0').replace(/[^\d.]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function cellValue(row: string[], index?: number): string {
  return index != null ? cellAt(row, index) : '';
}

function normalizeHeader(cell: string | undefined | null): string {
  return String(cell ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function detectColumnMap(headerRow: string[]): ColumnMap {
  const map: ColumnMap = {};
  const headers = headerRow.map(normalizeHeader);

  // RE&S consolidation first: "Item" = vendor code, "Item Name" = description.
  // Otherwise bare "Item" is treated as description and codes land in DESCRIPTION.
  const itemExactIdx = headers.findIndex((h) => h === 'item');
  const itemNameIdx = headers.findIndex(
    (h) => h === 'item name' || (h.includes('item') && h.includes('name')),
  );
  const hasItemNamePair = itemExactIdx >= 0 && itemNameIdx >= 0 && itemExactIdx !== itemNameIdx;
  if (hasItemNamePair) {
    map.items = itemExactIdx;
    map.description = itemNameIdx;
  }

  headers.forEach((h, index) => {
    if (!h) return;
    if (
      map.items == null &&
      (/^sku$|^(item\s*code|item\s*no|material\s*code|product\s*code)$/.test(h) ||
        (h.includes('item') && h.includes('code')))
    ) {
      map.items = index;
    }
  });

  headers.forEach((h, index) => {
    if (!h) return;
    if (map.items == null && h === 'code') map.items = index;
    // Prefer real description headers; bare "item" only when there is no Item Name column.
    if (
      map.description == null &&
      (/^product$|^description$|^(item\s*description|item\s*name)$/.test(h) ||
        (h.includes('item') && h.includes('name')) ||
        (h === 'item' && !hasItemNamePair)) &&
      index !== map.items
    ) {
      map.description = index;
    }
    // Prefer exact UOM over Packaging (RE&S has both).
    if (map.uom == null && (/^uom$|^unit$|^unit\s*of\s*measure$/.test(h) || (h.includes('purchase') && h.includes('unit')))) {
      map.uom = index;
    }
    if (
      map.unit_price == null &&
      /unit\s*price|unit\s*cost/i.test(h) &&
      !/total|line/i.test(h)
    ) {
      map.unit_price = index;
    }
    if (map.unit_price == null && /^price$/i.test(h) && !/total|line/i.test(h)) {
      map.unit_price = index;
    }
  });

  if (map.uom == null) {
    headers.forEach((h, index) => {
      if (!h) return;
      if (/^packaging$/.test(h)) map.uom = index;
    });
  }

  // Prefer "Qty. ordered" / order qty over "How much/many"
  headers.forEach((h, index) => {
    if (!h) return;
    if (/qty\.?\s*ordered|quantity\s*ordered|order\s*qty|ordered\s*qty/.test(h)) {
      map.quantity = index;
    }
  });
  if (map.quantity == null) {
    headers.forEach((h, index) => {
      if (!h) return;
      if (/^(qty|quantity|q\.?ty\.?)$/.test(h) || (h.includes('qty') && !/total/.test(h))) {
        map.quantity = index;
      }
    });
  }
  if (map.quantity == null) {
    headers.forEach((h, index) => {
      if (!h) return;
      if (h.includes('how much') || h.includes('how many')) map.quantity = index;
    });
  }

  return map;
}

function detectConsolidationColumnMap(headerRow: string[]): ConsolidationColumnMap | null {
  let outlet: number | undefined;
  let poNumber: number | undefined;
  let deliveryDate: number | undefined;

  headerRow.forEach((cell, index) => {
    const h = normalizeHeader(cell);
    if (!h) return;
    if (outlet == null && h === 'outlet') outlet = index;
    if (poNumber == null && /^po\s*(no|number|#)\.?$/i.test(h)) poNumber = index;
    if (
      deliveryDate == null &&
      (/^delivery\s*date$/i.test(h) || /^del\.?\s*date$/i.test(h) || h.includes('delivery') && h.includes('date'))
    ) {
      deliveryDate = index;
    }
  });

  if (outlet == null || poNumber == null) return null;

  const base = detectColumnMap(headerRow);
  if (base.quantity == null || (base.items == null && base.description == null)) return null;

  return { ...base, outlet, po_number: poNumber, delivery_date: deliveryDate };
}

function isSkippableRow(description: string, items: string): boolean {
  const text = `${description} ${items}`.trim().toLowerCase();
  if (!text) return true;
  if (PO_VENDOR_SKU_RE.test(items) || PO_VENDOR_SKU_RE.test(description)) return false;
  return /^(sub\s*total|subtotal|total|tax|gst|grand total|delivery|freight|amount|outlet|po\s*no)/.test(
    text,
  );
}

function inferQtyFromRow(row: string[], columnMap: ColumnMap, sku: string): number {
  if (columnMap.quantity != null) {
    const mapped = parseQty(cellValue(row, columnMap.quantity));
    if (mapped > 0) return mapped;
  }

  const candidates: number[] = [];
  for (let index = 0; index < row.length; index += 1) {
    if (index === columnMap.unit_price) continue;
    const trimmed = cellAt(row, index);
    if (!/^\d+$/.test(trimmed)) continue;
    const n = Number(trimmed);
    if (n <= 0 || n > 9999) continue;
    if (n <= 20 && index === 0) continue;
    if (trimmed === sku.replace(/\D/g, '').slice(0, trimmed.length)) continue;
    candidates.push(n);
  }

  if (candidates.length === 0) return 0;
  return candidates[candidates.length - 1]!;
}

function inferUomFromRow(row: string[], columnMap: ColumnMap): string {
  const mapped = cellValue(row, columnMap.uom);
  if (mapped) return mapped;
  for (let i = 0; i < row.length; i += 1) {
    const token = cellAt(row, i).toUpperCase();
    if (UOM_TOKENS.has(token)) return token;
  }
  return '';
}

function parsePriceCell(raw: string): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  const sgd = trimmed.match(/SGD\s*([\d,]+(?:\.\d+)?)/i);
  if (sgd) return sgd[1]!.replace(/,/g, '');
  const numeric = trimmed.replace(/[^\d.]/g, '');
  return numeric;
}

function inferUnitPriceFromRow(row: string[], columnMap: ColumnMap): string {
  const mapped = parsePriceCell(cellValue(row, columnMap.unit_price));
  if (mapped) return mapped;

  for (let i = 0; i < row.length; i += 1) {
    const match = cellAt(row, i).match(/SGD\s*([\d,]+(?:\.\d+)?)/i);
    if (match) return match[1]!.replace(/,/g, '');
  }

  for (let index = 0; index < row.length; index += 1) {
    if (index === columnMap.quantity) continue;
    if (index === columnMap.items) continue;
    if (index === columnMap.uom) continue;
    const trimmed = cellAt(row, index);
    if (/^\d+\.\d+$/.test(trimmed)) return trimmed;
  }

  return '';
}

function inferDescriptionFromRow(row: string[], columnMap: ColumnMap, sku: string): string {
  const mapped = cellValue(row, columnMap.description);
  if (mapped && !PO_VENDOR_SKU_RE.test(mapped) && mapped.toUpperCase() !== sku) return mapped;
  for (let i = 0; i < row.length; i += 1) {
    const trimmed = cellAt(row, i);
    if (!trimmed || PO_VENDOR_SKU_RE.test(trimmed)) continue;
    if (/^\d+$/.test(trimmed)) continue;
    if (UOM_TOKENS.has(trimmed.toUpperCase())) continue;
    if (/^SGD\b/i.test(trimmed)) continue;
    if (trimmed.length > 8) return trimmed;
  }
  return sku;
}

/** Label/value header block used by FoodMarketHub PurchOrder exports. */
function extractHeaderFieldsFromRows(rows: string[][]): {
  company_name: string;
  company_address: string;
  po_number: string;
  delivery_date: string;
} {
  let company_name = '';
  let company_address = '';
  let po_number = '';
  let delivery_date = '';

  const labelValue = (label: string): string => {
    const target = label.toLowerCase();
    for (const row of rows) {
      for (let i = 0; i < row.length; i += 1) {
        const cell = cellAt(row, i).toLowerCase().replace(/\s+/g, ' ');
        if (cell === target || cell.startsWith(`${target} `)) {
          // Prefer next non-empty cell on the same row (value column).
          for (let j = i + 1; j < row.length; j += 1) {
            const value = cellAt(row, j);
            if (value && value.toLowerCase() !== cell) return value.replace(/\s+/g, ' ').trim();
          }
        }
      }
    }
    return '';
  };

  po_number = labelValue('purchase order') || labelValue('po number') || labelValue('po no');
  delivery_date =
    labelValue('expected delivery date') || labelValue('delivery date') || labelValue('order date');
  company_name =
    labelValue('company name / entity name') ||
    labelValue('company name') ||
    labelValue('entity name') ||
    labelValue('shop number');
  company_address = labelValue('delivery address') || labelValue('address');

  return {
    company_name,
    company_address,
    po_number,
    delivery_date: parseDeliveryDateDmy(delivery_date),
  };
}

function buildLineItemRow(row: string[], columnMap: ColumnMap, relaxed = false): DemPoExtractRow | null {
  let items = cellValue(row, columnMap.items);
  let description = cellValue(row, columnMap.description);

  if (!items && relaxed) {
    items = findSkuInRow(row);
  }

  if (isSkippableRow(description, items)) return null;
  if (!items && !description) return null;

  const sku = items.match(PO_VENDOR_SKU_RE)?.[1]?.toUpperCase() ?? findSkuInRow(row);
  if (sku) items = sku;

  let qty = columnMap.quantity != null ? parseQty(cellValue(row, columnMap.quantity)) : 0;
  if (qty <= 0 && relaxed) {
    qty = inferQtyFromRow(row, columnMap, sku || items);
  }
  if (qty <= 0) return null;

  return {
    items: items || sku,
    description: description || inferDescriptionFromRow(row, columnMap, sku || items),
    quantity: String(qty),
    uom: cellValue(row, columnMap.uom) || (relaxed ? inferUomFromRow(row, columnMap) : ''),
    unit_price:
      parsePriceCell(cellValue(row, columnMap.unit_price)) ||
      (relaxed ? inferUnitPriceFromRow(row, columnMap) : ''),
  };
}

/** Count rows that look like vendor SKU line items (e.g. FoodMarketHub emails). */
export function countVendorSkuRows(rows: string[][]): number {
  let count = 0;
  for (const row of rows) {
    if (findSkuInRow(row)) count += 1;
  }
  return count;
}

function findLineItemsHeader(rows: string[][]): { headerIndex: number; columnMap: ColumnMap } | null {
  for (let i = 0; i < Math.min(40, rows.length); i += 1) {
    const candidate = detectColumnMap(rows[i] ?? []);
    const headerText = (rows[i] ?? []).join(' ').toLowerCase();
    const hasSkuHeader = headerText.includes('sku');
    if (
      candidate.quantity != null &&
      (candidate.items != null || candidate.description != null) &&
      (hasSkuHeader || candidate.items != null)
    ) {
      return { headerIndex: i, columnMap: candidate };
    }
  }
  return null;
}

/** RE&S-style consolidation tables: one order per Outlet + PO No (carry-forward blank cells). */
export function tryExtractConsolidationOrders(
  rows: string[][],
  fileName: string,
): DemPoExtractOrder[] | null {
  let headerIndex = -1;
  let columnMap: ConsolidationColumnMap | null = null;

  for (let i = 0; i < Math.min(80, rows.length); i += 1) {
    const candidate = detectConsolidationColumnMap(rows[i] ?? []);
    if (candidate) {
      headerIndex = i;
      columnMap = candidate;
      break;
    }
  }

  if (headerIndex < 0 || !columnMap) return null;

  const groups = new Map<string, DemPoExtractOrder>();
  let lastOutlet = '';
  let lastPo = '';
  let lastDate = '';

  for (let i = headerIndex + 1; i < rows.length; i += 1) {
    const row = rows[i] ?? [];
    if (cellValue(row, columnMap.outlet).toLowerCase() === 'outlet') continue;

    const outlet = cellValue(row, columnMap.outlet) || lastOutlet;
    const poNumber = cellValue(row, columnMap.po_number) || lastPo;
    const deliveryDateRaw = cellValue(row, columnMap.delivery_date) || lastDate;

    if (outlet) lastOutlet = outlet;
    if (poNumber) lastPo = poNumber;
    if (deliveryDateRaw) lastDate = deliveryDateRaw;

    if (!outlet || !poNumber) continue;

    const lineItem = buildLineItemRow(row, columnMap, true);
    if (!lineItem) continue;

    // Key = outlet + PO only (not raw delivery-date string — "1/10" vs "01/10" was over-splitting).
    const groupKey = `${outlet.trim().toLowerCase()}|${poNumber.trim().toLowerCase()}`;
    let order = groups.get(groupKey);
    if (!order) {
      order = {
        header: {
          company_name: outlet,
          company_address: '',
          po_number: poNumber,
          delivery_date: parseDeliveryDateDmy(deliveryDateRaw),
          source_file: fileName,
        },
        rows: [],
      };
      groups.set(groupKey, order);
    } else if (!order.header.delivery_date && deliveryDateRaw) {
      order.header.delivery_date = parseDeliveryDateDmy(deliveryDateRaw);
    }
    order.rows.push(lineItem);
  }

  const orders = [...groups.values()].filter((order) => order.rows.length > 0);
  return orders.length > 0 ? orders : null;
}

export function tryExtractOrdersFromRowMatrix(
  rows: string[][],
  fileName: string,
  options?: { relaxed?: boolean },
): DemPoExtractOrder[] | null {
  if (rows.length < 2) return null;
  const relaxed = options?.relaxed ?? false;

  const consolidation = tryExtractConsolidationOrders(rows, fileName);
  if (consolidation) return consolidation;

  const header = findLineItemsHeader(rows);
  if (!header) return null;

  const dataRows: DemPoExtractRow[] = [];
  const seen = new Set<string>();

  for (let i = header.headerIndex + 1; i < rows.length; i += 1) {
    const lineItem = buildLineItemRow(rows[i] ?? [], header.columnMap, relaxed);
    if (!lineItem) continue;
    const key = vendorSkuRowKey(lineItem);
    if (seen.has(key)) continue;
    seen.add(key);
    dataRows.push(lineItem);
  }

  if (dataRows.length === 0) return null;

  const headerFields = extractHeaderFieldsFromRows(rows.slice(0, header.headerIndex + 1));

  return [
    {
      header: {
        company_name: headerFields.company_name,
        company_address: headerFields.company_address,
        po_number: headerFields.po_number,
        delivery_date: headerFields.delivery_date,
        source_file: fileName,
      },
      rows: dataRows,
    },
  ];
}

export async function tryExtractOrdersFromSpreadsheet(file: File): Promise<DemPoExtractOrder[] | null> {
  if (!isExcelFileName(file.name)) return null;

  const rows = await readExcelRowsForExtract(file);
  const expectedSkus = countVendorSkuRows(rows);

  let orders = tryExtractOrdersFromRowMatrix(rows, file.name, { relaxed: false });
  const extracted = orders?.reduce((sum, order) => sum + order.rows.length, 0) ?? 0;

  if (orders && expectedSkus > extracted) {
    const relaxed = tryExtractOrdersFromRowMatrix(rows, file.name, { relaxed: true });
    const relaxedCount = relaxed?.reduce((sum, order) => sum + order.rows.length, 0) ?? 0;
    if (relaxedCount > extracted) orders = relaxed;
  }

  return orders;
}
